import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ApiError } from "@kkp/api-client";

import { useAuth } from "../app/providers/AuthProvider";
import { Logo } from "../components/branding/Logo";
import { Button } from "@kkp/ui";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@kkp/ui";
import { Field, Input } from "../ui/Input";
import { PasswordInput } from "@kkp/ui";
import { toast } from "@kkp/ui";

type LocationState = {
  from?: string;
};

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required.")
    .email("Enter a valid email address.")
    .max(255, "Email is too long."),
  password: z
    .string()
    .min(1, "Password is required.")
    .max(255, "Password is too long."),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state ?? {}) as LocationState;
  const from = state.from ?? "/dashboard";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  React.useEffect(() => {
    try {
      const expired = window.localStorage.getItem("kkp_client_session_expired");
      if (expired) {
        window.localStorage.removeItem("kkp_client_session_expired");
        toast.warning("Session expired. Please sign in again.", {
          title: "Session",
          durationMs: 2400,
        });
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const onSubmit = handleSubmit(async ({ email, password }) => {
    try {
      const outcome = await auth.login({ email: email.trim(), password });
      if (outcome.status === "browserConfirmationRequired") {
        navigate("/browser-confirmation", { replace: true });
        return;
      }
      toast.success("Signed in.", { title: "Welcome", durationMs: 2000 });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.httpStatus === 429) {
        toast.danger("Too many attempts. Please try again later.", {
          title: "Error",
        });
        return;
      }
      toast.danger("Login failed. Please try again.", {
        title: "Error",
      });
    }
  });

  return (
    <main className="kkp-auth">
      <div className="kkp-auth_stack">
        <Logo height={86} />

        <Card className="w-full max-w-[520px] p-5">
          <CardHeader className="pb-0 mb-0 border-b-0">
            <CardTitle as="h1">Client Login</CardTitle>
            <CardDescription>
              Please login with your client account credentials.
            </CardDescription>
          </CardHeader>

          <CardBody className="mt-4">
            <form className="grid gap-4" onSubmit={onSubmit}>
              <Field label="Email" required error={errors.email?.message}>
                <Input
                  autoComplete="email"
                  placeholder="name@company.com"
                  {...register("email")}
                />
              </Field>

              <Field label="Password" required error={errors.password?.message}>
                <PasswordInput
                  autoComplete="current-password"
                  placeholder="*******"
                  {...register("password")}
                />
              </Field>

              <div className="kkp-auth_actions">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Signing in…" : "Login"}
                </Button>
              </div>

              <p className="text-sm text-center">
                <a
                  className="text-blue-600 hover:underline"
                  href="#/forgot-password"
                >
                  Forgot password?
                </a>
              </p>
            </form>
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
